// Studio's state: one small store, read with hooks. Pages read it and call
// actions (actions.ts); they don't fetch. Only the walkthrough settings are
// kept, in this browser (guide-state.ts).

import { useEffect, useReducer, useRef } from "preact/hooks";
import type { AuthkitConfig, AuthUser } from "../../../ui/src/index.ts";
import type { AgentActionTranscript, AgentActivityResponse, WaitingInteractionsResponse } from "../domain/agent.ts";
import type { GoldenResult } from "../domain/golden.ts";
import type { Sample } from "../domain/sample.ts";
import { storedSeen, storedToursOn, type TourId } from "./guide-state.ts";
import type { ApiFailure, HubRecord, HubSecretsSummary, DemandResponse, EventsResponse, GoldenResponse, StudioHubs, ToolsetsResponse, ActivationsResponse } from "../domain/types.ts";

/** `authkit` is null where the environment doesn't configure sign-in, as in local-prod; there Studio reads nothing, since the Curator API needs a token. */
/** `consoleUrl`: the Console in the same environment, for the wordmark's app switcher and Ask it in the Console. */
export type Config = { env: string; label: string; apiBase: string; consoleUrl: string; authkit: AuthkitConfig | null };
export type Load<T> = { status: "loading" } | { status: "ok"; value: T; at: number } | { status: "failed"; error: ApiFailure };

/** A golden set's test runs, one question at a time. A failed one stops the run and keeps its error. */
export type GoldenRun = {
  hub: HubRecord; staged: boolean; total: number; results: GoldenResult[];
  status: "running" | "stopped" | "done" | "failed"; error?: ApiFailure;
};

export type StudioState = {
  config: Config;
  sample: Sample;
  /** Who's signed in with WarmHub, or null. */
  user: AuthUser | null;
  hubs: Load<StudioHubs> | null;
  /** By Hub ID. The newest 200 events, the most the Curator API returns. */
  events: Record<string, Load<EventsResponse>>;
  /** By "<Hub ID>:<days>". */
  demand: Record<string, Load<DemandResponse>>;
  /** By Hub ID. A hub's newest toolsets. */
  toolsets: Record<string, Load<ToolsetsResponse>>;
  /** By Hub ID. A hub's newest activations. */
  activations: Record<string, Load<ActivationsResponse>>;
  /** By Hub ID. The hub's secrets' states, read for its Hub token's. */
  hubSecrets: Record<string, Load<HubSecretsSummary>>;
  /** By Hub ID. The hub agent's newest agent actions, and its Interactions. */
  agent: Record<string, Load<AgentActivityResponse>>;
  /** By "<Hub ID> <action ID>". One agent action's curated transcript. */
  agentActions: Record<string, Load<AgentActionTranscript>>;
  /** By "<Hub ID> <Interaction ID>". The Exchange's words for a Curator reply it kept that the hub agent hasn't taken up yet. */
  agentReplies: Record<string, string>;
  /** The Interactions waiting on the curator across every hub they curate: "needs you". */
  waitingInteractions: Load<WaitingInteractionsResponse> | null;
  /** Golden questions for every hub the caller curates. */
  golden: Load<GoldenResponse> | null;
  /** The newest golden set run. It keeps going while the curator reads other pages. */
  goldenRun: GoldenRun | null;
  /** The page's help drawer. */
  helpOpen: boolean;
  /** The walkthrough that's running, if any. */
  tour: { id: TourId; step: number } | null;
  toursOn: boolean;
  seenTours: TourId[];
};

export const initialState = (config: Config, sample: Sample, user: AuthUser | null = null): StudioState => ({
  config, sample, user, hubs: null, events: {}, demand: {}, toolsets: {}, activations: {}, hubSecrets: {}, agent: {}, agentActions: {}, agentReplies: {}, waitingInteractions: null, golden: null, goldenRun: null,
  helpOpen: false, tour: null, toursOn: storedToursOn(), seenTours: storedSeen(),
});

let state: StudioState;
const listeners = new Set<() => void>();

export const getState = (): StudioState => state;
export function initStore(s: StudioState) { state = s; }
export function setState(change: Partial<StudioState> | ((s: StudioState) => Partial<StudioState>)) {
  state = { ...state, ...(typeof change === "function" ? change(state) : change) };
  listeners.forEach((l) => l());
}

/** Reads part of the state with this render's selector; the component re-renders when that part changes. */
export function useStore<T>(select: (s: StudioState) => T): T {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const value = select(state);
  // The subscription outlives this render, so it reads the latest selector and
  // value through refs: a selector that depends on props or the route stays current.
  const latest = useRef({ select, value });
  latest.current = { select, value };
  useEffect(() => {
    const update = () => { if (!Object.is(latest.current.select(state), latest.current.value)) rerender(0); };
    listeners.add(update);
    update();
    return () => { listeners.delete(update); };
  }, []);
  return value;
}
